import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface Props {
  title: string;
  lastUpdated: string;
  icon: React.ReactNode;
  onBack: () => void;
  onNavigatePrivacy?: () => void;
  onNavigateTerms?: () => void;
  children: React.ReactNode;
}

export const LegalPage: React.FC<Props> = ({ 
  title, 
  lastUpdated, 
  icon, 
  onBack, 
  onNavigatePrivacy, 
  onNavigateTerms, 
  children 
}) => {
  return (
    <div
      className="min-h-screen bg-background-light flex flex-col font-sans text-irongrey selection:bg-primary selection:text-white"
      data-extracted-from="owned:ystnj/public/legal-root"
    >
      {/* Header */}
      <header
        className="px-6 md:px-12 h-20 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-20 border-b border-gray-100/50"
        data-extracted-from="owned:ystnj/public/legal-header"
      >
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-irongrey/60 hover:text-irongrey transition-colors group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back</span>
        </button>
        <div className="text-right">
          <h2 className="text-sm font-bold text-irongrey">{title}</h2>
          <p className="text-[10px] text-irongrey/40 font-bold uppercase tracking-widest">Legal Information</p>
        </div>
      </header>

      {/* Main Content */}
      <main
        className="flex-1 max-w-5xl mx-auto w-full px-6 md:px-12 py-12 pb-20"
        data-extracted-from="owned:ystnj/public/legal-main"
      >
        <div className="flex flex-col items-center mb-12">
           <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mb-6 text-irongrey/80">
             {icon}
           </div>
           <h1 className="text-4xl font-bold text-irongrey mb-3 text-center tracking-tight">{title}</h1>
           <p className="text-irongrey/50 italic font-medium">Last Updated: {lastUpdated}</p>
        </div>

        <div
          className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 md:p-12"
          data-extracted-from="owned:ystnj/public/legal-content-card"
        >
          <div className="prose prose-slate max-w-none prose-headings:font-bold prose-headings:text-irongrey prose-h1:text-xl prose-h2:text-lg prose-p:text-irongrey/80 prose-p:leading-relaxed prose-li:text-irongrey/80 prose-li:marker:text-irongrey/40 prose-strong:text-irongrey prose-a:text-primary hover:prose-a:text-primary-hover">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer
        className="bg-white border-t border-gray-100 mt-auto py-10"
        data-extracted-from="owned:ystnj/public/legal-footer"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p className="text-xs font-semibold text-irongrey">© 2026 <a className="hover:text-primary transition-colors underline decoration-primary/30" href="https://intelliolabs.com" target="_blank" rel="noreferrer">Intellio, Inc.</a> All rights reserved.</p>
            <p className="text-[10px] text-irongrey/50 uppercase tracking-widest font-bold">Secure Education Management System</p>
          </div>
          <div className="flex items-center gap-8">
            <button 
              onClick={onNavigatePrivacy}
              className={`text-xs font-medium transition-colors ${title === 'Privacy Policy' ? 'text-primary font-bold' : 'text-irongrey/70 hover:text-[#237062]'}`}
            >
              Privacy Policy
            </button>
            <button 
              onClick={onNavigateTerms}
              className={`text-xs font-medium transition-colors ${title === 'Terms and Conditions' ? 'text-primary font-bold' : 'text-irongrey/70 hover:text-[#237062]'}`}
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};
